import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import { styled, useTheme } from "@mui/material/styles";
import { INCOMLEND_INTERNAL } from "codes";
import useIsMobile from "hooks/useIsMobile";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "redux/hooks";

import Header from "./Header";
import ProfileSidebar from "./ProfileSidebar";
import Sidebar from "./Sidebar";
import LeadCommentsModal from "components/Common/LeadCommentsModal";

const drawerWidth: number = 260;

const getActiveDrawer = (openMenu: boolean, openProfile: boolean) => {
  const openMenuMultiplier = openMenu ? 1 : 0;
  const openProfileMultiplier = openProfile ? 1 : 0;
  return openMenuMultiplier + openProfileMultiplier;
};

const Main = styled("main", {
  shouldForwardProp: (prop) =>
    prop !== "openMenu" && prop !== "theme" && prop !== "openProfile",
})<{ openMenu: boolean; openProfile: boolean }>(
  ({ theme, openMenu, openProfile }) => ({
    backgroundColor: theme.palette.background.paper,
    width: "100%",
    minHeight: "calc(100vh - 88px)",
    flexGrow: 1,
    padding: "10px",
    marginTop: "88px",
    marginRight: "0px",
    marginLeft: openProfile ? "20px" : 0,
    borderRadius: "12px",
    transition: theme.transitions.create(
      "margin",
      openMenu || openProfile
        ? {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }
        : {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }
    ),
    zIndex: 1099, // Set a higher z-index than the drawer
    [theme.breakpoints.up("md")]: {
      marginLeft: openMenu ? 0 : -(drawerWidth - 20),
      marginRight: openProfile ? 0 : -(drawerWidth - 20),
      width: `calc(100% - ${drawerWidth * getActiveDrawer(openMenu, openProfile) + 20 * getActiveDrawer(openMenu, openProfile)}px)`,
    },
    [theme.breakpoints.down("md")]: {
      marginLeft: "20px",
      width: `calc(100% - ${drawerWidth}px)`,
      padding: "10px",
    },
    [theme.breakpoints.down("sm")]: {
      marginLeft: "10px",
      width: `calc(100% - ${drawerWidth}px)`,
      padding: "10px",
      marginRight: "10px",
    },
  })
);

const SIDEBAR_OPEN_KEY = "dashboard-sidebar-open";

function getInitialOpenMenu(isMobile: boolean): boolean {
  try {
    const stored = sessionStorage.getItem(SIDEBAR_OPEN_KEY);
    if (stored !== null) return stored === "true";
  } catch {
    // ignore
  }
  return !isMobile;
}

export default function Dashboard({
  children,
  mastheader,
}: {
  children: React.ReactNode;
  mastheader?: React.ReactNode;
}) {
  const isMobile = useIsMobile();
  const [openMenu, setOpenMenu] = React.useState(() =>
    getInitialOpenMenu(isMobile)
  );
  const [openProfile, setOpenProfile] = React.useState(false);

  // Persist sidebar open state so it survives remounts
  React.useEffect(() => {
    try {
      sessionStorage.setItem(SIDEBAR_OPEN_KEY, String(openMenu));
    } catch {
      // ignore
    }
  }, [openMenu]);

  const location = useLocation();
  const APP_PERMISSION = useAppSelector((state) => state.appState.permission);
  // On Trade Partners (list + Add Supplier/Buyer), keep sidebar open so all menu options stay visible (client-only; internal unchanged)
  const isTradePartnersRoute = location.pathname.startsWith("/trading-relationships");
  const isInternal = APP_PERMISSION === INCOMLEND_INTERNAL;
  React.useEffect(() => {
    if (!isInternal && isTradePartnersRoute && !isMobile && !openMenu) {
      setOpenMenu(true);
    }
  }, [isInternal, isTradePartnersRoute, isMobile, openMenu]);

  // State for lead comment modal
  const [isLeadCommentModalOpen, setIsLeadCommentModalOpen] = React.useState(false);
  const [leadCommentData, setLeadCommentData] = React.useState<{
    leadId: string;
    leadName: string;
    accountId: string;
  } | null>(null);

  const handleLeftDrawerToggle = () => {
    setOpenMenu(!openMenu);
  };
  const handleRightDrawerToggle = () => {
    setOpenProfile(!openProfile);
  };
  const theme = useTheme();

  // Listen for the custom event to open lead comment modal
  React.useEffect(() => {
    const handleOpenModal = () => {
      const leadId = sessionStorage.getItem('leadCommentModalId');
      const leadName = sessionStorage.getItem('leadCommentModalName') || 'Lead';
      const accountId = sessionStorage.getItem('leadCommentEntityId') || '';

      if (leadId) {
        setLeadCommentData({ leadId, leadName, accountId });
        setIsLeadCommentModalOpen(true);
      }
    };

    window.addEventListener('openLeadCommentModal', handleOpenModal);
    return () => {
      window.removeEventListener('openLeadCommentModal', handleOpenModal);
    };
  }, []);

  return (
    <Box
      sx={{ display: "flex", bgcolor: theme.palette.background.default }}
      data-testid="dashboard-bounding-box"
    >
      <AppBar
        enableColorOnDark
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.default,
          transition: openMenu ? theme.transitions.create("width") : "none",
        }}
        data-testid="app-bar"
      >
        <Toolbar sx={{ bgcolor: theme.palette.background.default }}>
          <Header
            drawerOpen={openMenu}
            handleLeftDrawerToggle={handleLeftDrawerToggle}
            profileOpen={openProfile}
            handleRightDrawerToggle={handleRightDrawerToggle}
            data-testid="header"
          />
        </Toolbar>
      </AppBar>

      {/* drawer */}
      <Sidebar
        drawerOpen={openMenu}
        drawerToggle={handleLeftDrawerToggle}
        data-testid="sidebar"
      />

      {/* main content */}
      <Main
        theme={theme}
        openMenu={openMenu}
        openProfile={openProfile}
        data-testid="main-content"
      >
        <Box
          sx={{
            bgcolor: theme.palette.background.default,
            p: { lg: 1, xs: 1 },
            pt: { lg: 1 },
            borderRadius: "12px",
          }}
        > 
          {children}
        </Box>
      </Main>
      {/* drawer */}
      <ProfileSidebar
        drawerOpen={openProfile}
        drawerToggle={handleRightDrawerToggle}
        data-testid="profile-sidebar"
      />
      
      {/* Lead Comment Modal */}
      {leadCommentData && (
        <LeadCommentsModal
          open={isLeadCommentModalOpen}
          onClose={() => {
            setIsLeadCommentModalOpen(false);
            // Clear session storage when modal is closed
            sessionStorage.removeItem('openLeadCommentModal');
            sessionStorage.removeItem('leadCommentModalId');
            sessionStorage.removeItem('leadCommentModalName');
            sessionStorage.removeItem('leadCommentEntityId');
          }}
          leadId={leadCommentData.leadId}
          leadName={leadCommentData.leadName}
          accountId={leadCommentData.accountId}
        />
      )}
    </Box>
  );
}
